import { useState, useCallback } from "react";
import { View } from "react-native";
import { useQueryClient } from "@tanstack/react-query";
import type { TranslationKeys } from "@chinese-vendor/i18n";
import { extractApiError } from "@/lib/api/errors";
import { Modal } from "@/components/ui/Modal";
import { AppText } from "@/components/ui/AppText";
import { Button } from "@/components/ui/Button";
import { useI18n } from "@/context/I18nContext";
import { usersApi } from "@/lib/api/users";
import { SuccessState } from "@/components/ui/SuccessState";
import { ErrorState } from "@/components/ui/ErrorState";

type State = "confirmation" | "success" | "error";

interface ModalConfig {
  titleKey: TranslationKeys;
  bodyKey: TranslationKeys;
  confirmKey: TranslationKeys;
  cancelKey: TranslationKeys;
  successKey: TranslationKeys;
  successBodyKey: TranslationKeys;
}

const MODAL_CONFIG = {
  deactivate: {
    titleKey: "staff.users.deactivate.title",
    bodyKey: "staff.users.deactivate.body",
    confirmKey: "staff.users.deactivate.confirm",
    cancelKey: "staff.users.deactivate.cancel",
    successKey: "staff.users.deactivate.success",
    successBodyKey: "staff.users.deactivate.successBody",
  },
  reactivate: {
    titleKey: "staff.users.reactivate.title",
    bodyKey: "staff.users.reactivate.body",
    confirmKey: "staff.users.reactivate.confirm",
    cancelKey: "staff.users.reactivate.cancel",
    successKey: "staff.users.reactivate.success",
    successBodyKey: "staff.users.reactivate.successBody",
  },
} as const satisfies Record<string, ModalConfig>;

export interface DeactivateUserModalProps {
  visible: boolean;
  /** ID of the user to deactivate/reactivate. */
  userId: string;
  /** Display name of the user — shown in the confirmation body. */
  userName: string;
  /** Whether the user is currently active. Determines deactivate vs reactivate. */
  isActive: boolean;
  onDismiss: () => void;
  /** Called after a successful status change so the detail screen can refresh. */
  onSuccess?: () => void;
}

export function DeactivateUserModal({
  visible,
  userId,
  userName,
  isActive,
  onDismiss,
  onSuccess,
}: DeactivateUserModalProps) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const [submitting, setSubmitting] = useState(false);
  const [state, setState] = useState<State>("confirmation");
  const [error, setError] = useState<string | null>(null);
  // Snapshot the action when confirmed so config doesn't flip when isActive prop
  // changes after the query invalidation updates user.status.
  const [confirmedAction, setConfirmedAction] = useState<"deactivate" | "reactivate" | null>(null);

  const config =
    confirmedAction != null
      ? MODAL_CONFIG[confirmedAction]
      : isActive
        ? MODAL_CONFIG.deactivate
        : MODAL_CONFIG.reactivate;

  const reset = useCallback(() => {
    setState("confirmation");
    setSubmitting(false);
    setError(null);
    setConfirmedAction(null);
  }, []);

  const handleDismiss = useCallback(() => {
    reset();
    onDismiss();
  }, [reset, onDismiss]);

  const handleConfirm = useCallback(async () => {
    const action = isActive ? "deactivate" : "reactivate";
    setConfirmedAction(action);
    setSubmitting(true);
    try {
      if (isActive) {
        await usersApi.deactivateUser(userId);
      } else {
        await usersApi.reactivateUser(userId);
      }
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["users", "list"] }),
        queryClient.invalidateQueries({ queryKey: ["users", "detail", userId] }),
        queryClient.invalidateQueries({ queryKey: ["users", "activityLog", userId] }),
        queryClient.invalidateQueries({ queryKey: ["admin", "stats"] }),
      ]);
      setState("success");
      onSuccess?.();
    } catch (err) {
      setError(extractApiError(err, t("common.error.generic")));
      setState("error");
    } finally {
      setSubmitting(false);
    }
  }, [isActive, userId, queryClient, t, onSuccess]);

  return (
    <Modal
      visible={visible}
      onDismiss={handleDismiss}
      title={state === "confirmation" ? config.titleKey : null}
      closeOnBackdrop={!submitting}
      footer={
        state === "confirmation" ? (
          <View className="flex-row gap-3 justify-end">
            <Button
              label={config.cancelKey}
              onPress={handleDismiss}
              variant="tertiary"
              disabled={submitting}
            />
            <Button
              label={config.confirmKey}
              onPress={handleConfirm}
              variant="primary"
              loading={submitting}
            />
          </View>
        ) : null
      }
    >
      {state === "confirmation" && (
        <AppText className="text-sm text-dark-text-30">
          {t(config.bodyKey, { name: userName })}
        </AppText>
      )}

      {state === "success" && (
        <SuccessState
          title={config.successKey}
          description={config.successBodyKey}
          className="px-0 pt-0"
        />
      )}

      {state === "error" && (
        <ErrorState
          descriptionText={error || undefined}
          description="common.error.generic"
          className="px-0 pt-0"
        />
      )}
    </Modal>
  );
}
